import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PRODUCTS } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  product_interest: "",
  quantity: "",
  message: "",
};

export const QuoteForm = ({ compact = false }) => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.phone) {
      toast.error("Please fill in name, company, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        product_interest: form.product_interest || null,
        quantity: form.quantity || null,
        message: form.message || null,
      };
      await axios.post(`${API}/quotes`, payload);
      toast.success("Thank you! Our team will get back to you shortly.");
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not submit the request. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="quote-form"
      className={`grid grid-cols-1 ${
        compact ? "" : "md:grid-cols-2"
      } gap-5`}
    >
      <div className="space-y-2">
        <Label htmlFor="q-name" className="text-slate-900 font-semibold">
          Full Name *
        </Label>
        <Input
          id="q-name"
          data-testid="quote-input-name"
          value={form.name}
          onChange={handleChange("name")}
          placeholder="Your full name"
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="q-company" className="text-slate-900 font-semibold">
          Company *
        </Label>
        <Input
          id="q-company"
          data-testid="quote-input-company"
          value={form.company}
          onChange={handleChange("company")}
          placeholder="Company name"
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="q-email" className="text-slate-900 font-semibold">
          Email *
        </Label>
        <Input
          id="q-email"
          data-testid="quote-input-email"
          type="email"
          value={form.email}
          onChange={handleChange("email")}
          placeholder="name@company.com"
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="q-phone" className="text-slate-900 font-semibold">
          Phone *
        </Label>
        <Input
          id="q-phone"
          data-testid="quote-input-phone"
          value={form.phone}
          onChange={handleChange("phone")}
          placeholder="+91 ..."
          required
        />
      </div>
      <div className="space-y-2">
        <Label className="text-slate-900 font-semibold">Product Interest</Label>
        <Select
          value={form.product_interest || undefined}
          onValueChange={(v) =>
            setForm((f) => ({ ...f, product_interest: v }))
          }
        >
          <SelectTrigger data-testid="quote-select-product">
            <SelectValue placeholder="Select a product" />
          </SelectTrigger>
          <SelectContent>
            {PRODUCTS.map((p) => (
              <SelectItem
                key={p.code}
                value={p.code}
                data-testid={`quote-product-option-${p.code.toLowerCase()}`}
              >
                {p.code} — {p.name}
              </SelectItem>
            ))}
            <SelectItem value="Other" data-testid="quote-product-option-other">
              Other / Multiple
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="q-qty" className="text-slate-900 font-semibold">
          Estimated Quantity
        </Label>
        <Input
          id="q-qty"
          data-testid="quote-input-quantity"
          value={form.quantity}
          onChange={handleChange("quantity")}
          placeholder="e.g. 20 MT / month"
        />
      </div>
      <div className={`space-y-2 ${compact ? "" : "md:col-span-2"}`}>
        <Label htmlFor="q-msg" className="text-slate-900 font-semibold">
          Message
        </Label>
        <Textarea
          id="q-msg"
          data-testid="quote-input-message"
          value={form.message}
          onChange={handleChange("message")}
          placeholder="Tell us about your application, end-use, and delivery location..."
          rows={5}
        />
      </div>
      <div className={`${compact ? "" : "md:col-span-2"} flex justify-start`}>
        <Button
          type="submit"
          data-testid="quote-submit-button"
          disabled={submitting}
          className="gradient-accent text-white font-semibold uppercase tracking-wider rounded-md hover:opacity-90 transition-opacity h-12 px-8"
        >
          {submitting ? "Submitting..." : "Request a Quote"}
        </Button>
      </div>
    </form>
  );
};

export default QuoteForm;
